import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "search_history";
const MAX_HISTORY = 8;

export interface SearchHistoryItem {
  query: string;
  timestamp: number;
  type: "search" | "book" | "publisher" | "author" | "book_type" | "series";
  slug?: string;
}

export const useSearchHistory = () => {
  const [history, setHistory] = useState<SearchHistoryItem[]>([]);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as SearchHistoryItem[];
        setHistory(parsed);
      }
    } catch (e) {
      console.error("Failed to load search history:", e);
    }
  }, []);

  // Save to localStorage whenever history changes
  const saveHistory = useCallback((newHistory: SearchHistoryItem[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      setHistory(newHistory);
    } catch (e) {
      console.error("Failed to save search history:", e);
    }
  }, []);

  const addToHistory = useCallback((item: Omit<SearchHistoryItem, "timestamp">) => {
    setHistory((prev) => {
      // Remove duplicates (same query and type)
      const filtered = prev.filter(
        (h) => !(h.query.toLowerCase() === item.query.toLowerCase() && h.type === item.type)
      );
      
      const newItem: SearchHistoryItem = {
        ...item,
        timestamp: Date.now(),
      };
      
      // Add to beginning and limit to MAX_HISTORY
      const newHistory = [newItem, ...filtered].slice(0, MAX_HISTORY);
      
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      } catch (e) {
        console.error("Failed to save search history:", e);
      }
      
      return newHistory;
    });
  }, []);

  const removeFromHistory = useCallback((query: string, type: string) => {
    setHistory((prev) => {
      const newHistory = prev.filter(
        (h) => !(h.query.toLowerCase() === query.toLowerCase() && h.type === type)
      );
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      } catch (e) {
        console.error("Failed to save search history:", e);
      }
      return newHistory;
    });
  }, []);

  const clearHistory = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setHistory([]);
    } catch (e) {
      console.error("Failed to clear search history:", e);
    }
  }, []);

  return {
    history,
    addToHistory,
    removeFromHistory,
    clearHistory,
  };
};
